/**
 * Auth context for the seller portal (M19).
 *
 * Wraps the centralised api client with React state for the current
 * user, the bearer token, and a `loading` flag the routes use to
 * gate their first render.
 *
 * Public surface:
 *   const { user, token, loading, login, register, logout, refreshUser } = useAuth();
 *
 * The seller portal's login is unique: it requires a reference number
 * in addition to email/password. The reference number is also stored
 * in localStorage so the dashboard can fetch quotations on mount.
 *
 * The `bc:auth:logout` window event lets api.js force a logout from a
 * 401 response without importing this module.
 */

import { createContext, useContext, useEffect, useState } from "react";

import api from "../services/api";

const TOKEN_KEY = "token";
const REF_KEY = "referenceNumber";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get("/auth/me");
        if (!cancelled) setUser(res.data);
      } catch {
        if (!cancelled) handleLogout();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const onForcedLogout = () => handleLogout();
    window.addEventListener("bc:auth:logout", onForcedLogout);
    return () => window.removeEventListener("bc:auth:logout", onForcedLogout);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REF_KEY);
    setToken(null);
    setUser(null);
  };

  const login = async (email, password, referenceNumber) => {
    const res = await api.post("/auth/login", {
      email,
      password,
      reference_number: referenceNumber,
    });
    const { access_token, user: userData } = res.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(REF_KEY, referenceNumber);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, surname, idNumber, popiaConsent) => {
    const res = await api.post("/auth/register", {
      name,
      surname,
      id_number: idNumber,
      email,
      password,
      popia_consent: popiaConsent,
    });
    return res.data;
  };

  const logout = handleLogout;

  // Re-fetch the current user from /auth/me and update cached state.
  // Used after a profile edit so the welcome greeting reflects changes.
  const refreshUser = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
